import { assertNotNull } from '../../../helpers/nullHelpers';
import { numericInputStringToNumber } from '../../../helpers/stringHelpers';
import {
  CreateUpdateDesignRoomDetailsDto,
  mapDesignRoomDetailsFormModelToDto,
} from '../../designs/createUpdateDesignRoomDetailsDto';
import { FloorLevelCode, ThermostatTypeCode } from '../quote-or-design-form/quoteOrDesignFormData';
import { RoomFormModel } from '../quote-or-design-form/rooms/roomFormModel';
import { RoomType } from '../quote-or-design-form/rooms/roomTypes';
import {
  CreateUpdateRoomHeatLossDetailsDto,
  mapRoomHeatLossDetailsFormModelToDto,
} from './heatLoss/createUpdateRoomHeatLossDetailsDto';

export type QuoteCreateUpdateRoomDto = CreateUpdateRoomDto;

export type DesignCreateUpdateRoomDto = CreateUpdateRoomDto & {
  heatLossDetails: CreateUpdateRoomHeatLossDetailsDto | null;
  designRoomDetails: CreateUpdateDesignRoomDetailsDto;
};

export type CreateUpdateRoomDto = {
  roomId: number | null;
  id: string;
  name: string;
  displayIndex: number;
  roomTemperature: number | null;
  floorLevelCode: FloorLevelCode | null;
  floorConstructionPartId: number | null;
  areaInMetresSquared: number | null;
  thermostatTypeCode: ThermostatTypeCode;
  thermostatSharedWithId: string | null;
  numberOfThermostats: number | null;
  numberOfWetSensors: number | null;
  numberOfFloorSensors: number | null;
  numberOfPorts: number | null;
  roomType: RoomType | null;
  skirtingHeating: CreateUpdateRoomSkirtingHeatingDetailsDto | null;
};

export const mapRoomFormModelToQuoteDto = (
  formModel: RoomFormModel,
  roomIndex: number,
  belongsToRadiatorManifold: boolean,
): QuoteCreateUpdateRoomDto => ({
  ...mapRoomFormModelToDto(formModel, roomIndex, belongsToRadiatorManifold),
});

export const mapRoomFormModelToDesignDto = (
  formModel: RoomFormModel,
  roomIndex: number,
  belongsToRadiatorManifold: boolean,
): DesignCreateUpdateRoomDto => ({
  ...mapRoomFormModelToDto(formModel, roomIndex, belongsToRadiatorManifold),
  heatLossDetails:
    formModel.heatLossDetails == null ? null : mapRoomHeatLossDetailsFormModelToDto(formModel.heatLossDetails),
  designRoomDetails: mapDesignRoomDetailsFormModelToDto(assertNotNull(formModel.designRoomDetails)),
});

const mapRoomFormModelToDto = (
  formModel: RoomFormModel,
  roomIndex: number,
  belongsToRadiatorManifold: boolean,
): CreateUpdateRoomDto => ({
  roomId: formModel.roomId,
  id: formModel.id,
  name: formModel.name,
  roomType: formModel.roomType,
  displayIndex: roomIndex,
  floorLevelCode: formModel.floorLevelCode,
  floorConstructionPartId: formModel.floorConstruction?.partId ?? null,
  roomTemperature: numericInputStringToNumber(formModel.roomTemperature),
  areaInMetresSquared: numericInputStringToNumber(formModel.areaInMetresSquared),
  thermostatTypeCode: formModel.thermostatTypeCode,
  thermostatSharedWithId:
    typeof formModel.thermostatSharedWithId === 'string' ? formModel.thermostatSharedWithId : null,
  numberOfThermostats: belongsToRadiatorManifold ? 0 : numericInputStringToNumber(formModel.numberOfThermostats),
  numberOfWetSensors: belongsToRadiatorManifold ? 0 : numericInputStringToNumber(formModel.numberOfWetSensors),
  numberOfFloorSensors: belongsToRadiatorManifold ? 0 : numericInputStringToNumber(formModel.numberOfFloorSensors),
  numberOfPorts:
    belongsToRadiatorManifold || formModel.floorConstruction?.isElectricUnderfloorHeating
      ? null
      : numericInputStringToNumber(formModel.numberOfPorts),
  skirtingHeating: belongsToRadiatorManifold ? null : getSkirtingHeatingDtoFromRoomFormModel(formModel),
});

export type CreateUpdateRoomSkirtingHeatingDetailsDto = {
  perimeterInMetres: number;
};

export const getSkirtingHeatingDtoFromRoomFormModel = (
  room: RoomFormModel,
): CreateUpdateRoomSkirtingHeatingDetailsDto | null => {
  const perimeterInMetres = numericInputStringToNumber(room.perimeterInMetres);
  return perimeterInMetres != null
    ? {
        perimeterInMetres: assertNotNull(numericInputStringToNumber(room.perimeterInMetres)),
      }
    : null;
};
